import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { Notifica } from '../api/types'
import { formatDateTime, internalPath } from './common'
import { usePolling } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * La campanella delle notifiche, in alto a destra.
 *
 * Il pallino conta le voci **non lette**; il menu le elenca, dalla più
 * recente, con il link alla pagina dove si agisce. «Segna lette» azzera il
 * pallino senza toccare gli allarmi (restano da decidere nelle loro pagine):
 * confondere le due cose farebbe sparire un allarme vero con un gesto pensato
 * per pulire il badge.
 */
export default function NotificheBell() {
  const { data, reload } = usePolling(() => api.notifiche(), 60000)
  const [aperta, setAperta] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Un clic fuori chiude il menu: senza, resta aperto sopra il resto e va
  // chiuso ripremendo la campanella, che nessuno indovina.
  useEffect(() => {
    if (!aperta) return
    function fuori(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setAperta(false)
    }
    document.addEventListener('mousedown', fuori)
    return () => document.removeEventListener('mousedown', fuori)
  }, [aperta])

  const count = data?.count ?? 0
  const items = data?.items ?? []

  async function apri() {
    const prossimo = !aperta
    setAperta(prossimo)
    // Aprendo il menu si segna letto: si è guardato. Le voci restano visibili
    // in questa apertura, ma il pallino sparisce.
    if (prossimo && count > 0) {
      await api.notificheLette()
      await reload()
    }
  }

  function vai(n: Notifica) {
    setAperta(false)
    navigate(internalPath(n.link))
  }

  // Il titolo si traduce dal **tipo** con `t('…')` letterale, non da `t(n.title)`:
  // il testo del titolo arriva dal server (italiano) e, passato come variabile,
  // sfuggirebbe alla guardia delle traduzioni e resterebbe in italiano.
  function titolo(n: Notifica): string {
    switch (n.kind) {
      case 'dobermann':
        return t('Indirizzo mai censito')
      case 'honeypot':
        return t("Connessione a un'esca")
      case 'eagle':
        return t('Parametro in allarme')
      case 'contract_expiring':
        return t('Contratto in scadenza')
      case 'contract_expired':
        return t('Contratto scaduto')
      default:
        return n.title
    }
  }

  return (
    <div className="notif" ref={box}>
      <button
        type="button"
        className="notif__campana topbar__button"
        onClick={apri}
        aria-label={t('Notifiche')}
        aria-expanded={aperta}
        title={t('Notifiche')}
      >
        <Campana />
        {count > 0 && <span className="notif__pallino">{count > 99 ? '99+' : count}</span>}
      </button>

      {aperta && (
        <div className="notif__menu" role="menu">
          <div className="notif__testata">{t('Notifiche')}</div>
          {items.length === 0 ? (
            <p className="notif__vuoto">{t('Niente che chieda attenzione.')}</p>
          ) : (
            <ul className="notif__lista">
              {items.map((n) => (
                <li key={n.id}>
                  <button type="button" className={`notif__voce notif__voce--${n.severity ?? 'ok'}`} onClick={() => vai(n)}>
                    <span className="notif__corpo">
                      <span className="notif__titolo">
                        {titolo(n)}
                        {n.unread && <span className="notif__nuovo" aria-hidden="true" />}
                      </span>
                      <span className="notif__dettaglio mono">{n.detail}</span>
                      {n.at && <span className="notif__quando">{formatDateTime(n.at)}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function Campana() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true">
      <path d="M6 9 a6 6 0 0 1 12 0 c0 4 1.2 6 2 7 H4 c0.8 -1 2 -3 2 -7 Z" />
      <path d="M10 20 a2 2 0 0 0 4 0" />
    </svg>
  )
}
